import { useEffect, useState } from "react";
import { Search, Plus, Flame } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import api from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import { resolveImage } from "@/lib/image";

export default function Restaurant() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [filter, setFilter] = useState("All");
  const [q, setQ] = useState("");
  const [selectedSizes, setSelectedSizes] = useState({});
  const [comboSelections, setComboSelections] = useState({});
  const [riceSelections, setRiceSelections] = useState({});
  const { addItem } = useCart();

  useEffect(() => {
    api.get("/menu").then(({ data }) => setItems(data)).catch(() => {});
    api.get("/menu/categories").then(({ data }) => setCats(["All", ...data])).catch(() => {});
  }, []);

  const filtered = items.filter((i) =>
    (filter === "All" || i.category === filter) &&
    (q === "" || i.name.toLowerCase().includes(q.toLowerCase()))
  );

  const saleType = (it) => it.sale_type || (it.combo_eligible ? "combo" : it.party_tray_eligible ? "party_tray" : "ala_carte");

  const getSelectedSize = (it) => {
    if (saleType(it) === "combo" || !it.sizes?.length) return null;
    const selected = selectedSizes[it.id] || it.sizes[0].label;
    return it.sizes.find((s) => s.label === selected) || it.sizes[0];
  };

  const getPrice = (it) => {
    const selectedSize = getSelectedSize(it);
    return Number(selectedSize?.price ?? it.price ?? 0);
  };

  const toggleComboEntree = (itemId, entree, max) => {
    const current = comboSelections[itemId] || [];
    const next = current.includes(entree)
      ? current.filter((x) => x !== entree)
      : current.length < max
        ? [...current, entree]
        : current;
    setComboSelections({ ...comboSelections, [itemId]: next });
  };

  const add = (it) => {
    const selectedSize = getSelectedSize(it);
    const price = getPrice(it);
    const type = saleType(it);
    const entrees = comboSelections[it.id] || [];
    const rice = riceSelections[it.id] || it.rice_options?.[0] || "";
    const itemType = it.is_lechon ? "lechon" : type === "party_tray" ? "catering" : "restaurant";

    if (type === "combo" && entrees.length !== Number(it.combo_entree_count || 0)) {
      toast.error(`Please select exactly ${it.combo_entree_count} entree(s).`);
      return;
    }

    addItem({
      item_type: itemType,
      item_id: it.id,
      name: selectedSize ? `${it.name} (${selectedSize.label})` : it.name,
      unit_price: price,
      image: it.image,
      options: type === "combo"
        ? { combo_entrees: entrees, rice }
        : selectedSize
          ? { size: selectedSize.label }
          : {},
    });

    toast.success(`${it.name}${selectedSize ? ` - ${selectedSize.label}` : ""} added to cart`);
  };

  return (
    <div className="chow-theme bg-[#050505] min-h-screen text-[#FAF9F6]" data-testid="restaurant-page">
      <section className="border-b border-[#D4AF37]/30 py-16 text-center">
        <BrandLogo type="restaurant" size="xl" showText={false} className="justify-center mb-3" />
        <span className="font-accent text-xs text-[#D4AF37]">CHOWMAHARLIKA</span>
        <h1 className="font-heading text-5xl lg:text-6xl text-[#FAF9F6] mt-2">
          Timeless Filipino Traditions <span className="italic text-[#D4AF37]">on every plate</span>
        </h1>
        <div className="gold-divider max-w-md mx-auto mt-6"><BrandLogo type="restaurant" size="xs" showText={false} className="justify-center" /></div>
        <p className="mt-4 text-[#FAF9F6]/60 max-w-xl mx-auto">Authentic recipes · Fresh ingredients · Crafted with love</p>
      </section>

      <section className="border-b border-[#D4AF37]/20 sticky top-0 z-30 bg-[#050505]/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-4 flex flex-wrap gap-3 items-center justify-between">
          <div className="flex gap-2 overflow-x-auto no-scrollbar flex-1">
            {cats.map((c) => (
              <button
                key={c}
                data-testid={`menu-cat-${c.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setFilter(c)}
                className={`whitespace-nowrap px-4 py-2 text-xs font-accent uppercase tracking-wider transition-colors ${
                  filter === c ? "bg-[#D4AF37] text-[#050505]" : "text-[#D4AF37] border border-[#D4AF37]/40 hover:bg-[#D4AF37]/10"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="relative">
            <Search className="w-4 h-4 text-[#D4AF37] absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="menu-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search dishes..."
              className="bg-[#111] border border-[#D4AF37]/30 text-[#FAF9F6] pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#D4AF37]"
            />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((it) => {
          const selectedSize = getSelectedSize(it);
          const price = getPrice(it);

          return (
            <article key={it.id} className="chow-card overflow-hidden" data-testid={`menu-item-${it.id}`}>
              {it.image && <div className="h-44 bg-cover bg-center" style={{ backgroundImage: `url(${resolveImage(it.image)})` }} />}

              <div className="p-5">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="font-heading text-2xl text-[#D4AF37] leading-tight">{it.name}</h3>
                  <span className="font-heading text-xl text-[#FAF9F6]">${price.toFixed(2)}</span>
                </div>

                {saleType(it) !== "combo" && it.sizes?.length > 0 && (
                  <div className="my-3">
                    <label className="text-xs text-[#FAF9F6]/50 block mb-1">Size</label>
                    <select
                      value={selectedSize?.label || ""}
                      onChange={(e) => setSelectedSizes({ ...selectedSizes, [it.id]: e.target.value })}
                      className="w-full bg-[#111] border border-[#D4AF37]/30 text-[#FAF9F6] px-3 py-2 rounded"
                    >
                      {it.sizes.map((s) => (
                        <option key={s.label} value={s.label}>
                          {s.label} — ${Number(s.price || 0).toFixed(2)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {saleType(it) === "combo" && (
                  <div className="my-3 border border-[#D4AF37]/20 rounded p-3">
                    {it.rice_options?.length > 0 && (
                      <div className="mb-3">
                        <label className="text-xs text-[#FAF9F6]/50 block mb-1">Rice</label>
                        <select
                          value={riceSelections[it.id] || it.rice_options[0]}
                          onChange={(e) => setRiceSelections({ ...riceSelections, [it.id]: e.target.value })}
                          className="w-full bg-[#111] border border-[#D4AF37]/30 text-[#FAF9F6] px-3 py-2 rounded"
                        >
                          {it.rice_options.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                    )}

                    <div className="text-xs text-[#FAF9F6]/50 mb-2">
                      Choose {it.combo_entree_count} entree(s): {(comboSelections[it.id] || []).length}/{it.combo_entree_count}
                    </div>
                    <div className="grid grid-cols-1 gap-1 max-h-40 overflow-y-auto">
                      {(it.combo_choices || []).map((choice) => (
                        <label key={choice} className="flex items-center gap-2 text-xs text-[#FAF9F6]/80">
                          <input
                            type="checkbox"
                            checked={(comboSelections[it.id] || []).includes(choice)}
                            onChange={() => toggleComboEntree(it.id, choice, Number(it.combo_entree_count || 0))}
                          />
                          {choice}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {it.spicy_level > 0 && (
                  <div className="flex items-center gap-1 text-xs text-[#D4AF37] mb-2">
                    {[...Array(it.spicy_level)].map((_, i) => <Flame key={i} className="w-3 h-3" />)} <span>Spicy</span>
                  </div>
                )}

                <p className="text-sm text-[#FAF9F6]/65 leading-relaxed mb-4 line-clamp-2">{it.description}</p>

                <div className="flex items-center justify-between">
                  <div className="flex gap-1.5 flex-wrap">
                    {it.is_lechon && <span className="badge-royal">LECHON</span>}
                    {saleType(it) === "party_tray" && <span className="badge-royal">PARTY TRAY</span>}
                    {saleType(it) === "combo" && <span className="badge-royal">COMBO</span>}
                    {it.is_special && <span className="badge-royal">SPECIAL</span>}
                    {it.featured && <span className="badge-royal">FEATURED</span>}
                  </div>

                  <button
                    data-testid={`menu-add-${it.id}`}
                    onClick={() => add(it)}
                    className="w-9 h-9 rounded-full bg-[#D4AF37] text-[#050505] hover:bg-[#E8D08D] flex items-center justify-center transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </article>
          );
        })}

        {filtered.length === 0 && (
          <div className="col-span-full text-center py-16 text-[#FAF9F6]/40">No dishes match your search.</div>
        )}
      </section>
    </div>
  );
}
